import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { AxiosError } from 'axios'
import type {
    AnnouncementSummary,
    AnnouncementDetail,
    AnnouncementRecipient,
    CampaignSummary,
    CreateCampaignRequest,
    CreateAnnouncementDraftRequest,
    CommunicationAudienceSelector,
    CommunicationAsset,
    CommunicationDetail,
	CommunicationEmailSend,
	CommunicationDeliveryJob,
    CommunicationRecipient,
    CommunicationSummary,
    ResolveCommunicationRecipientsRequest,
    ResolveCommunicationRecipientsResponse,
    ResolveStudentsRequest,
    ResolveStudentsResponse,
    SendCommunicationEmailRequest,
    SendCommunicationEmailResponse,
    UpdateCampaignRequest,
    UpdateAnnouncementDraftRequest,
    StudentFeedNotificationSubscriptions,
} from './types'
import { portalApi } from '@services/api/portalApi'

export const communicationAudienceSelectorsQueryOptions = () => queryOptions({
    queryKey: ['communications', 'audience-selectors'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
        const response = await portalApi.get<CommunicationAudienceSelector[]>('/communications/audience-selectors')
        return response.data
    },
})

export function useCommunicationAudienceSelectors() {
    return useQuery(communicationAudienceSelectorsQueryOptions())
}

export const announcementsQueryOptions = () => queryOptions({
    queryKey: ['communications', 'announcements'],
    queryFn: async () => {
        const response = await portalApi.get<AnnouncementSummary[]>('/communications/announcements')
        return response.data
    },
})

export function useAnnouncements() {
    return useQuery(announcementsQueryOptions())
}

export const studentFeedNotificationSubscriptionsQueryOptions = (enabled = true) => queryOptions({
    queryKey: ['communications', 'student-feed-notification-subscribers'],
    enabled,
    queryFn: async () => (await portalApi.get<StudentFeedNotificationSubscriptions>('/communications/student-feed-notification-subscribers')).data,
})

export function useStudentFeedNotificationSubscriptions(enabled = true) {
    return useQuery(studentFeedNotificationSubscriptionsQueryOptions(enabled))
}

export function useSetMyStudentFeedNotificationSubscription() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (subscribed: boolean) => subscribed
            ? portalApi.put('/communications/student-feed-notification-subscribers/me')
            : portalApi.delete('/communications/student-feed-notification-subscribers/me'),
        onSuccess: async (_response, subscribed) => {
            notifications.show({ color: 'green', message: subscribed ? 'Student feed Teams notifications enabled.' : 'Student feed Teams notifications disabled.' })
            await queryClient.invalidateQueries({ queryKey: studentFeedNotificationSubscriptionsQueryOptions().queryKey })
        },
        onError: (error) => notifications.show({ color: 'red', title: 'Notification preference was not changed', message: getCommunicationErrorMessage(error) }),
    })
}

export function useAddStudentFeedNotificationSubscriber() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (staffInitials: string) => portalApi.post('/communications/student-feed-notification-subscribers', { staffInitials }),
        onSuccess: async () => queryClient.invalidateQueries({ queryKey: studentFeedNotificationSubscriptionsQueryOptions().queryKey }),
        onError: (error) => notifications.show({ color: 'red', title: 'Staff member was not added', message: getCommunicationErrorMessage(error) }),
    })
}

export function useRemoveStudentFeedNotificationSubscriber() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (staffInitials: string) => portalApi.delete(`/communications/student-feed-notification-subscribers/${encodeURIComponent(staffInitials)}`),
        onSuccess: async () => queryClient.invalidateQueries({ queryKey: studentFeedNotificationSubscriptionsQueryOptions().queryKey }),
        onError: (error) => notifications.show({ color: 'red', title: 'Staff member was not removed', message: getCommunicationErrorMessage(error) }),
    })
}

export function useCreateAnnouncementDraft() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (request: CreateAnnouncementDraftRequest) => {
            const response = await portalApi.post<AnnouncementDetail>('/communications/announcements/drafts', request)
            return response.data
        },
        onSuccess: async () => {
            notifications.show({ color: 'green', message: 'Post draft saved.' })
            await queryClient.invalidateQueries({ queryKey: announcementsQueryOptions().queryKey })
        },
        onError: (error) => {
            notifications.show({ color: 'red', title: 'Draft was not saved', message: getCommunicationErrorMessage(error) })
        },
    })
}

export const announcementDetailQueryOptions = (id: string | null) => queryOptions({
    queryKey: ['communications', 'announcements', id],
    enabled: Boolean(id),
    queryFn: async () => (await portalApi.get<AnnouncementDetail>(`/communications/announcements/${id}`)).data,
})

export function useAnnouncementDetail(id: string | null) {
    return useQuery(announcementDetailQueryOptions(id))
}

export const announcementRecipientsQueryOptions = (id: string | null) => queryOptions({
    queryKey: ['communications', 'announcements', id, 'recipients'],
    enabled: Boolean(id),
    queryFn: async () => (await portalApi.get<AnnouncementRecipient[]>(`/communications/announcements/${id}/recipients`)).data,
})

export function useAnnouncementRecipients(id: string | null) {
    return useQuery(announcementRecipientsQueryOptions(id))
}

function useAnnouncementMutation(action: 'draft' | 'publish' | 'withdraw' | 'reset-to-draft') {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, request }: { id: string; request?: UpdateAnnouncementDraftRequest }) => action === 'draft'
            ? (await portalApi.put<AnnouncementDetail>(`/communications/announcements/${id}/draft`, request)).data
            : (await portalApi.post<AnnouncementDetail>(`/communications/announcements/${id}/${action}`)).data,
        onSuccess: async (detail) => {
            queryClient.setQueryData(announcementDetailQueryOptions(detail.summary.id).queryKey, detail)
            await queryClient.invalidateQueries({ queryKey: announcementsQueryOptions().queryKey })
        },
        onError: (error) => notifications.show({ color: 'red', title: `Post ${action} failed`, message: getCommunicationErrorMessage(error) }),
    })
}

export const useUpdateAnnouncementDraft = () => useAnnouncementMutation('draft')
export const usePublishAnnouncement = () => useAnnouncementMutation('publish')
export const useWithdrawAnnouncement = () => useAnnouncementMutation('withdraw')
export const useResetAnnouncementToDraft = () => useAnnouncementMutation('reset-to-draft')

export function useDeleteAnnouncement() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: string) => portalApi.delete(`/communications/announcements/${id}`),
        onSuccess: async (_response, id) => {
            queryClient.removeQueries({ queryKey: announcementDetailQueryOptions(id).queryKey })
            await queryClient.invalidateQueries({ queryKey: announcementsQueryOptions().queryKey })
            notifications.show({ color: 'green', message: 'Post deleted.' })
        },
        onError: (error) => notifications.show({ color: 'red', title: 'Post was not deleted', message: getCommunicationErrorMessage(error) }),
    })
}

export const communicationAssetsQueryOptions = (communicationId: string | null) => queryOptions({
    queryKey: ['communications', communicationId, 'assets'],
    enabled: Boolean(communicationId),
    queryFn: async () => (await portalApi.get<CommunicationAsset[]>(`/communications/announcements/${communicationId}/assets`)).data,
})

export function useCommunicationAssets(communicationId: string | null) {
    return useQuery(communicationAssetsQueryOptions(communicationId))
}

export function useUploadCommunicationAsset(communicationId: string | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ file, purpose, altText }: { file: File; purpose: 'embedded-image' | 'attachment'; altText?: string }) => {
            if (!communicationId) throw new Error('Save the post before uploading files.')
            const data = new FormData()
            data.append('file', file)
            data.append('purpose', purpose)
            if (altText) data.append('altText', altText)
            return (await portalApi.post<CommunicationAsset>(`/communications/announcements/${communicationId}/assets`, data)).data
        },
        onSuccess: async () => queryClient.invalidateQueries({ queryKey: communicationAssetsQueryOptions(communicationId).queryKey }),
        onError: (error) => notifications.show({ color: 'red', title: 'File upload failed', message: getCommunicationErrorMessage(error) }),
    })
}

export async function uploadCommunicationAsset(
    communicationId: string,
    { file, purpose, altText }: { file: File; purpose: 'embedded-image' | 'attachment'; altText?: string },
) {
    const data = new FormData()
    data.append('file', file)
    data.append('purpose', purpose)
    if (altText) data.append('altText', altText)
    return (await portalApi.post<CommunicationAsset>(`/communications/announcements/${communicationId}/assets`, data)).data
}

export function useDeleteCommunicationAsset(communicationId: string | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (assetId: string) => portalApi.delete(`/communications/announcements/${communicationId}/assets/${assetId}`),
        onSuccess: async () => queryClient.invalidateQueries({ queryKey: communicationAssetsQueryOptions(communicationId).queryKey }),
    })
}

export async function downloadCommunicationAsset(communicationId: string, asset: CommunicationAsset) {
    const response = await portalApi.get<Blob>(`/communications/announcements/${communicationId}/assets/${asset.id}/content`, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = asset.originalFileName
    anchor.click()
    URL.revokeObjectURL(url)
}

export async function getCommunicationAssetObjectUrl(communicationId: string, assetId: string) {
    const response = await portalApi.get<Blob>(`/communications/announcements/${communicationId}/assets/${assetId}/content`, { responseType: 'blob' })
    return URL.createObjectURL(response.data)
}

export const campaignsQueryOptions = () => queryOptions({
    queryKey: ['communications', 'campaigns'],
    queryFn: async () => {
        const response = await portalApi.get<CampaignSummary[]>('/communications/campaigns')
        return response.data
    },
})

export function useCampaigns(enabled = true) {
    return useQuery({ ...campaignsQueryOptions(), enabled })
}

export function useCreateCampaign() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (request: CreateCampaignRequest) =>
            (await portalApi.post<CampaignSummary>('/communications/campaigns', request)).data,
        onSuccess: async () => {
            notifications.show({ color: 'green', message: 'Campaign created.' })
            await queryClient.invalidateQueries({ queryKey: campaignsQueryOptions().queryKey })
        },
        onError: (error) => notifications.show({ color: 'red', title: 'Campaign was not created', message: getCommunicationErrorMessage(error) }),
    })
}

export function useUpdateCampaign() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, request }: { id: string; request: UpdateCampaignRequest }) =>
            (await portalApi.put<CampaignSummary>(`/communications/campaigns/${id}`, request)).data,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: campaignsQueryOptions().queryKey })
            await queryClient.invalidateQueries({ queryKey: announcementsQueryOptions().queryKey })
        },
        onError: (error) => notifications.show({ color: 'red', title: 'Campaign was not updated', message: getCommunicationErrorMessage(error) }),
    })
}

export function useDeleteCampaign() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: string) => portalApi.delete(`/communications/campaigns/${id}`),
        onSuccess: async () => {
            notifications.show({ color: 'green', message: 'Campaign deleted. Its communications are now standalone.' })
            await queryClient.invalidateQueries({ queryKey: campaignsQueryOptions().queryKey })
            await queryClient.invalidateQueries({ queryKey: announcementsQueryOptions().queryKey })
        },
        onError: (error) => notifications.show({ color: 'red', title: 'Campaign was not deleted', message: getCommunicationErrorMessage(error) }),
    })
}

export function useAssignCommunicationCampaign() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ communicationId, campaignId }: { communicationId: string; campaignId: string | null }) =>
            portalApi.patch(`/communications/${communicationId}/campaign`, { campaignId }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: campaignsQueryOptions().queryKey })
            await queryClient.invalidateQueries({ queryKey: announcementsQueryOptions().queryKey })
        },
        onError: (error) => notifications.show({ color: 'red', title: 'Campaign membership was not changed', message: getCommunicationErrorMessage(error) }),
    })
}

export const communicationsQueryOptions = () => queryOptions({
    queryKey: ['communications'],
    queryFn: async () => {
        const response = await portalApi.get<CommunicationSummary[]>('/communications')
        return response.data
    },
})

export const communicationDetailQueryOptions = (id: string | null) => queryOptions({
    queryKey: ['communications', id],
    enabled: Boolean(id),
    queryFn: async () => {
        const response = await portalApi.get<CommunicationDetail>(`/communications/${id}`)
        return response.data
    },
})

export function useCommunications() {
    return useQuery(communicationsQueryOptions())
}

export const emailCommunicationsQueryOptions = () => queryOptions({
    queryKey: ['communications', 'email'],
	retry: false,
    queryFn: async () => {
        const response = await portalApi.get<CommunicationSummary[]>('/communications/email')
        return response.data
    },
})

export function useEmailCommunications() {
    return useQuery(emailCommunicationsQueryOptions())
}

export const communicationEmailSendsQueryOptions = () => queryOptions({
    queryKey: ['communications', 'email-sends'],
	retry: false,
	queryFn: async () => {
		const response = await portalApi.get<CommunicationEmailSend[]>('/communications/email/sends')
		return response.data
	},
})

export function useCommunicationEmailSends() {
	return useQuery(communicationEmailSendsQueryOptions())
}

export const communicationDeliveryQueueQueryOptions = () => queryOptions({
    queryKey: ['communications', 'email-delivery-queue'],
    retry: false,
    queryFn: async () => {
        const response = await portalApi.get<CommunicationDeliveryJob[]>('/communications/email/delivery-queue')
        return response.data
    },
})

export function useCommunicationDeliveryQueue() {
    return useQuery(communicationDeliveryQueueQueryOptions())
}

export function useRefreshCommunicationEmailSendStatus() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (recipientId: string) =>
            (await portalApi.post<CommunicationEmailSend>(`/communications/email/sends/${recipientId}/refresh-status`)).data,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: communicationEmailSendsQueryOptions().queryKey })
            await queryClient.invalidateQueries({ queryKey: communicationDeliveryQueueQueryOptions().queryKey })
            await queryClient.invalidateQueries({ queryKey: emailCommunicationsQueryOptions().queryKey })
        },
        onError: (error) => {
            notifications.show({ color: 'red', title: 'SMTP2GO status could not be refreshed', message: getCommunicationErrorMessage(error) })
        },
    })
}

export function useCommunicationDetail(id: string | null) {
    return useQuery(communicationDetailQueryOptions(id))
}

export function useResolveCommunicationRecipients(request: ResolveCommunicationRecipientsRequest, enabled = true) {
    const hasSelections = request.students.length > 0
        || request.to.length > 0
        || request.cc.length > 0
        || request.bcc.length > 0

    return useQuery({
        queryKey: ['communications', 'recipient-resolution', request],
        enabled: enabled && hasSelections,
        queryFn: async () => {
            const response = await portalApi.post<ResolveCommunicationRecipientsResponse>('/communications/recipients/resolve', request)
            return response.data
        },
    })
}

export function useResolveStudents(request: ResolveStudentsRequest, enabled = true) {
    return useQuery({
        queryKey: ['communications', 'student-resolution', request],
        enabled: enabled && request.selectors.length > 0,
        queryFn: async () => {
            const response = await portalApi.post<ResolveStudentsResponse>('/communications/students/resolve', request)
            return response.data
        },
    })
}

export function useSendCommunicationEmail() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (request: SendCommunicationEmailRequest) => {
            const response = await portalApi.post<SendCommunicationEmailResponse>('/communications/email/send', request)
            return response.data
        },
        onSuccess: async (response) => {
            notifications.show({
                color: 'green',
                message: `Queued ${response.recipientCount} individual send${response.recipientCount === 1 ? '' : 's'}.`,
            })
            await queryClient.invalidateQueries({ queryKey: communicationsQueryOptions().queryKey })
			await queryClient.invalidateQueries({ queryKey: emailCommunicationsQueryOptions().queryKey })
			await queryClient.invalidateQueries({ queryKey: communicationEmailSendsQueryOptions().queryKey })
			await queryClient.invalidateQueries({ queryKey: communicationDeliveryQueueQueryOptions().queryKey })
            await queryClient.invalidateQueries({ queryKey: communicationDetailQueryOptions(response.communicationId).queryKey })
        },
        onError: (error) => {
            notifications.show({
                color: 'red',
                title: 'Email was not sent',
                message: getCommunicationErrorMessage(error),
            })
        },
    })
}

export function useRetryCommunicationRecipient(communicationId: string | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (recipientId: string) => {
            if (!communicationId) throw new Error('No communication selected.')
            const response = await portalApi.post<CommunicationRecipient>(`/communications/${communicationId}/recipients/${recipientId}/retry`)
            return response.data
        },
        onSuccess: async () => {
            notifications.show({ color: 'green', message: 'Recipient retry queued.' })
            await queryClient.invalidateQueries({ queryKey: communicationsQueryOptions().queryKey })
			await queryClient.invalidateQueries({ queryKey: emailCommunicationsQueryOptions().queryKey })
			await queryClient.invalidateQueries({ queryKey: communicationEmailSendsQueryOptions().queryKey })
			await queryClient.invalidateQueries({ queryKey: communicationDeliveryQueueQueryOptions().queryKey })
            if (communicationId) {
                await queryClient.invalidateQueries({ queryKey: communicationDetailQueryOptions(communicationId).queryKey })
            }
        },
        onError: (error) => {
            notifications.show({
                color: 'red',
                title: 'Retry was not queued',
                message: getCommunicationErrorMessage(error),
            })
        },
    })
}

function getCommunicationErrorMessage(error: unknown) {
    if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
            return 'The .NET API rejected the request. Sign in again or check the Azure API client/tenant configuration.'
        }

        const responseMessage = (error.response?.data as { message?: string; error?: string } | undefined)?.message
            ?? (error.response?.data as { message?: string; error?: string } | undefined)?.error
        if (responseMessage) return responseMessage

        return error.message
    }

    return error instanceof Error ? error.message : 'An unexpected communications error occurred.'
}
